import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { Stage, Layer, Image as KonvaImage } from 'react-konva'

import { imageEditingActions, DrawingLine } from '../../reducers/imageEditing'
import { cursorStyle } from '../../utils/cursorStyles'
import { SquareLine } from '../edit_after_photo/shapes'
import { useImageEditingDrawingEvents } from './useImageEditingDrawingEvents'
import { applyMosaicToMaskedArea, blobToDataUrl } from '../../utils/mosaicUtils'

/**
 * 画像編集用の描画コンポーネント
 * Konvaを使用してキャンバス上でマスク描画機能を提供
 * マウスを離した時にモザイクが即座に適用される
 */
const ImageEditingDrawing = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const stageWrapRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<any>(null)

  const [stageWidth, setStageWidth] = useState(0)
  const [stageHeight, setStageHeight] = useState(0)
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)
  const [isProcessingMosaic, setIsProcessingMosaic] = useState(false)
  // 前回の線の数（変化検出用）
  const prevLinesCountRef = useRef(0)

  const {
    imageEditingDrawingLines,
    tempImageEditingDrawingLines,
    imageEditingBrushHead,
    imageEditingBrushSize,
    imageEditingDrawingOriginalImageUrl,
    showImageEditingDrawing,
    imageEditingMosaicHistoryStack,
    imageEditingMosaicHistoryIndex,
  } = useSelector((state: any) => state.imageEditing)

  // 現在表示すべき画像URL（履歴から取得、なければ元画像）
  const currentImageUrl = imageEditingMosaicHistoryIndex >= 0
    ? imageEditingMosaicHistoryStack[imageEditingMosaicHistoryIndex]
    : imageEditingDrawingOriginalImageUrl

  // 描画イベントフック
  const { handleMouseDown, handleMouseMove, handleMouseUp, showCursor, hideCursor } =
    useImageEditingDrawingEvents(stageWrapRef)

  // 画像の読み込み（現在の履歴位置の画像を表示）
  useEffect(() => {
    if (!currentImageUrl) return

    const img = new Image()
    // blob URLやdata URLの場合はcrossOriginを設定しない
    if (!currentImageUrl.startsWith('blob:') && !currentImageUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous'
    }
    img.onload = () => {
      setImageElement(img)
    }
    img.onerror = (e) => {
      console.error('[ImageEditingDrawing] Failed to load image:', e)
    }
    img.src = currentImageUrl
  }, [currentImageUrl])

  // ステージサイズの計算
  useEffect(() => {
    if (!stageWrapRef.current || !imageElement) return

    const maxStageHeight =
      window.innerHeight - 11 * parseInt(getComputedStyle(document.documentElement).fontSize)
    const maxStageWidth = stageWrapRef.current.clientWidth

    let newStageHeight = imageElement.height * (maxStageWidth / imageElement.width)
    let newStageWidth: number

    if (newStageHeight <= maxStageHeight) {
      newStageWidth = maxStageWidth
    } else {
      newStageHeight = maxStageHeight
      newStageWidth = imageElement.width * (maxStageHeight / imageElement.height)
    }

    setStageHeight(newStageHeight)
    setStageWidth(newStageWidth)
    stageWrapRef.current.style.height = newStageHeight + 'px'
  }, [imageElement])

  // stageRefをReduxに保存（imageElementが読み込まれた後に実行）
  useEffect(() => {
    if (stageRef.current && showImageEditingDrawing && imageElement) {
      const stage = stageRef.current.getStage()
      dispatch(imageEditingActions.setImageEditingDrawingStageRef(stage))
    }
  }, [showImageEditingDrawing, imageElement, dispatch])

  // ポップアップが開いた時に元画像を履歴の最初に追加
  useEffect(() => {
    if (showImageEditingDrawing && imageEditingDrawingOriginalImageUrl && imageEditingMosaicHistoryStack.length === 0) {
      dispatch(imageEditingActions.pushMosaicHistory(imageEditingDrawingOriginalImageUrl))
    }
  }, [showImageEditingDrawing, imageEditingDrawingOriginalImageUrl, imageEditingMosaicHistoryStack.length, dispatch])

  // ポップアップが閉じた時に履歴をリセット
  useEffect(() => {
    if (!showImageEditingDrawing) {
      prevLinesCountRef.current = 0
    }
  }, [showImageEditingDrawing])

  // 線が確定されたらモザイク処理を実行
  useEffect(() => {
    const currentLinesCount = imageEditingDrawingLines.length

    // 線が増えた場合のみ処理（削除やUndoは除外）
    if (currentLinesCount > prevLinesCountRef.current && stageRef.current && !isProcessingMosaic && currentImageUrl) {
      const applyMosaic = async () => {
        setIsProcessingMosaic(true)
        try {
          const stage = stageRef.current.getStage()

          // モザイク処理を実行
          const mosaicBlob = await applyMosaicToMaskedArea(stage, currentImageUrl, {
            mosaicRatio: 0.2,   // モザイクの粗さ（元のPython設定と同じ）
            longLen: 768,       // 長辺リサイズ（元のPython設定と同じ）
            redThreshold: 200,  // 赤チャンネル >= 200
            otherThreshold: 100, // G,B <= 100
          })

          // BlobからData URLを作成
          const mosaicDataUrl = await blobToDataUrl(mosaicBlob)

          // モザイク結果を履歴に追加
          dispatch(imageEditingActions.pushMosaicHistory(mosaicDataUrl))
          // モザイク結果URLを保存（確定ボタン用）
          dispatch(imageEditingActions.setImageEditingMosaicResultUrl(mosaicDataUrl))
          // 描画済みの線をクリア（モザイク画像に含まれるため）
          dispatch(imageEditingActions.clearImageEditingDrawingLines())
        } catch (error) {
          console.error('[ImageEditingDrawing] モザイク処理に失敗:', error)
        } finally {
          setIsProcessingMosaic(false)
        }
      }

      applyMosaic()
    }

    prevLinesCountRef.current = currentLinesCount
  }, [imageEditingDrawingLines.length, currentImageUrl, isProcessingMosaic, dispatch])

  // 線を描画するヘルパー関数（四角のみ）
  const renderLine = (line: DrawingLine, index: number, prefix: string, opacity: number) => {
    const color = `rgba(255, 0, 0, ${opacity})`

    return (
      <SquareLine
        key={`${prefix}-${index}`}
        line={line}
        stroke={color}
        fill={color}
        opacity={1}
      />
    )
  }

  return (
    <div ref={stageWrapRef} className="image-editing-drawing-wrapper relative flex justify-center">
      <Stage
        ref={stageRef}
        width={stageWidth}
        height={stageHeight}
        className="mx-auto"
        style={{ cursor: cursorStyle(imageEditingBrushHead, imageEditingBrushSize) }}
        onMouseEnter={showCursor}
        onMouseLeave={hideCursor}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {/* 背景画像レイヤー */}
        <Layer>
          {imageElement && (
            <KonvaImage
              image={imageElement}
              width={stageWidth}
              height={stageHeight}
            />
          )}
        </Layer>

        {/* マスク描画レイヤー */}
        <Layer listening={false}>
          {/* 確定済みのマスク線（不透明度1.0で純粋な赤色） */}
          {imageEditingDrawingLines.map((line: DrawingLine, i: number) =>
            renderLine(line, i, 'confirmed', 1.0)
          )}

          {/* 描画中の一時的なマスク線（不透明度0.5で視認しやすく） */}
          {tempImageEditingDrawingLines.map((line: DrawingLine, i: number) =>
            renderLine(line, i, 'temp', 0.5)
          )}
        </Layer>
      </Stage>
    </div>
  )
}

export default ImageEditingDrawing
